import {
  type Stripe,
  type StripeElements,
  type StripeError,
  type StripeExpressCheckoutElement,
  type StripeExpressCheckoutElementClickEvent,
  type StripeExpressCheckoutElementConfirmEvent,
  type StripeExpressCheckoutElementOptions,
  type StripeExpressCheckoutElementReadyEvent,
  type StripePaymentElement,
  type StripePaymentElementChangeEvent,
  type StripePaymentElementOptions,
  loadStripe,
} from '@stripe/stripe-js'
import {
  type ComputedRef,
  type PropType,
  type Ref,
  computed,
  defineComponent,
  h,
  onBeforeUnmount,
  onMounted,
  readonly,
  ref,
  shallowRef,
  watch,
} from 'vue'

import { buildAppearance, scrapeFontLinks } from './appearance'
import { isChargeable } from './helpers'
import { createStripeActions } from './stripeActions'
import type { PaymentMethodSurface, StripeElementsStatus, UseStripeElementsOptions } from './types'

export type UseStripeElementsReturn = ReturnType<typeof useStripeElements>

export function useStripeElements(options: UseStripeElementsOptions) {
  const stripe = shallowRef<Stripe | null>(null)
  const elements = shallowRef<StripeElements | null>(null)
  /* Terminal Element lifecycle only (`loadStripe` / `loaderror` / mount). Not checkout method results — those return from submit/createPaymentMethod/confirmPayment and never land on `fatalError`. */
  const fatalError = shallowRef<Error | StripeError | null>(null)
  const pmcRef: Ref<string> | null = options.pmcId ?? null
  const lockedTypes: readonly string[] | null = options.paymentMethodTypes ?? null

  /**
   *  Computed values
   */

  // Full payment-method surface for element creation. Reactive on `pmcRef`;
  // `lockedTypes` is immutable, so this only recomputes in the pmc branch.
  const paymentMethods: ComputedRef<PaymentMethodSurface> = computed(() => {
    if (lockedTypes) {
      // Stripe SDK wants a mutable `string[]`; copy the readonly input.
      return { paymentMethodTypes: [...lockedTypes] }
    }
    return { paymentMethodConfiguration: pmcRef!.value }
  })

  const status: ComputedRef<StripeElementsStatus> = computed(() => {
    if (fatalError.value) return 'error'
    if (elements.value) return 'ready'
    if (!isChargeable(options.amount.value)) return 'invalid_amount'
    return 'loading'
  })

  /**
   *  Lifecycle management
   */

  onMounted(async () => {
    try {
      const loaded = await loadStripe(options.publishableKey, { stripeAccount: options.accountId })
      if (!loaded) throw new Error('loadStripe returned null — the Stripe SDK may be blocked')

      stripe.value = loaded
      const amount = options.amount.value

      if (isChargeable(amount)) createElements(loaded, amount)
    } catch (e) {
      fatalError.value = e instanceof Error ? e : new Error(String(e))
    }
  })

  watch(
    (): readonly [number | null, string | null] => [options.amount.value, pmcRef?.value ?? null],
    ([newAmount]) => {
      // A terminal init failure (loadStripe failure or element `loaderror`) is
      // not recoverable in place — never (re)create or update the element. The
      // caller must remount (`:key`). `elements` is already null at this point
      // (cleared on failure); re-assert it defensively.
      if (fatalError.value || !stripe.value) {
        elements.value = null
        return
      }

      if (!isChargeable(newAmount)) {
        elements.value = null
        return
      }

      if (!elements.value) {
        // Mount handler hadn't finished loading, the amount started below the
        // minimum, or it was torn down on an earlier downward transition.
        // Create the element now that it's chargeable.
        createElements(stripe.value, newAmount)
        return
      }

      elements.value.update({
        amount: newAmount,
        ...paymentMethods.value,
      })
    },
  )

  /**
   *  Helper functions
   */

  function createElements(stripeInstance: Stripe, amount: number): void {
    elements.value = stripeInstance.elements({
      mode: 'payment',
      amount,
      currency: options.currencyCode.toLowerCase(),
      paymentMethodCreation: 'manual',
      appearance: buildAppearance(document.documentElement),
      fonts: options.fonts ?? scrapeFontLinks(),
      ...paymentMethods.value,
    })
  }

  /**
   *  Owns the Payment Element (iframe) lifecycle end-to-end: create on the live
   *  `Elements` instance, mount into its own DOM node, wire Stripe's events, and
   *  tear down. The `elements` ref it closes over is recreated/torn down by the
   *  amount/pmcId watcher above; this component reacts to that identity change to
   *  (re)mount or unmount, keeping element and instance lifecycle in one file.
   */
  const PaymentElement = defineComponent({
    name: 'TixStripePaymentElement',
    props: {
      /** Read once at create time (except `readOnly`, which has its own reactive prop). */
      options: {
        type: Object as PropType<Omit<StripePaymentElementOptions, 'fields' | 'readOnly'>>,
        required: false,
      },
      /**
       * Locks the Payment Element via Stripe's `readOnly` option so payment details can't change mid-checkout.
       * Reactive — updates call `paymentElement.update({ readOnly })`.
       */
      readOnly: {
        type: Boolean,
        default: false,
      },
    },
    // `null` declares an event with no runtime validator and a loosely-typed
    // payload — used for the events whose payload we forward but never inspect
    // (they only signal that something happened). `change` keeps the function
    // form because the param type *is* the payload type consumers rely on.
    emits: {
      ready: null,
      change: (event: StripePaymentElementChangeEvent) => event.elementType === 'payment',
      focus: null,
      blur: null,
      escape: null,
    },
    setup(props, { emit }) {
      const mountTarget = ref<HTMLDivElement | null>(null)
      let paymentElement: StripePaymentElement | null = null

      onMounted(mountPaymentElement)
      watch(elements, (instance) => (instance ? mountPaymentElement() : teardownPaymentElement()))
      watch(
        () => props.readOnly,
        (readOnly) => {
          paymentElement?.update({ readOnly })
        },
      )
      onBeforeUnmount(teardownPaymentElement)

      function mountPaymentElement(): void {
        /* v8 ignore next - defensive re-entrancy guard: `elements` only ever flips null→instance, 
        so onMounted (runs while null) and the later watch never both mount -- @preserve */
        if (paymentElement) return

        // `stripeElementsInstance` is null whenever the element can't/shouldn't show (invalid_amount,
        //  loading, or torn down).
        const stripeElementsInstance = elements.value
        if (!stripeElementsInstance) return

        const target = mountTarget.value
        /* v8 ignore if - `target` can't actually be null while mounted (the render fn always
         * renders the div with no `v-if`), but we guard defensively rather than
         * assert. -- @preserve
         */
        if (!target) {
          fatalError.value = new Error('Cannot mount payment element')
          elements.value = null
          return
        }

        paymentElement = stripeElementsInstance.create('payment', {
          paymentMethodOrder: ['card'],
          layout: {
            type: 'accordion',
            defaultCollapsed: false,
            radios: 'if_multiple',
          },
          ...props.options,
          // After `options` so the dedicated prop wins over a stale options.readOnly.
          readOnly: props.readOnly,
          // These wallets are presented by the Express Checkout Element
          wallets: {
            applePay: 'never',
            googlePay: 'never',
            link: 'never',
          },
          // `fields` is AFTER the consumer spread so it can't be overridden.
          // billingDetails MUST stay `'never'`: the element is built for manual
          // PaymentMethod creation, so billing details are supplied by the integrator
          // via `createPaymentMethod`. `billingDetails` is the only key in Stripe's `fields`
          fields: {
            billingDetails: {
              address: 'never',
              name: 'never',
              email: 'never',
            },
          },
        })

        paymentElement.mount(target)
        paymentElement.on('ready', (event) => emit('ready', event))
        paymentElement.on('change', (event) => emit('change', event))
        paymentElement.on('focus', (event) => emit('focus', event))
        paymentElement.on('blur', (event) => emit('blur', event))
        paymentElement.on('escape', (event) => emit('escape', event))
        paymentElement.on('loaderror', (event) => {
          // Load failures route onto the composable's single `fatalError` channel, not
          // a public emit. Terminal: tear the dead element down so the iframe
          // unmounts, the amount/pmcId watcher can't touch a failed instance, and
          // callers can't `submit()` against it. Recovery needs a remount (`:key`).
          fatalError.value = new Error(event.error.message ?? `Stripe element load error (${event.error.type})`)
          elements.value = null
        })
      }

      function teardownPaymentElement(): void {
        if (paymentElement) {
          paymentElement.unmount()
          paymentElement.destroy()
          paymentElement = null
        }
      }

      return () => h('div', { ref: mountTarget })
    },
  })

  /**
   *  Wallet buttons (Apple Pay / Google Pay). Mounts on the same `Elements`
   *  instance as the Payment Element, so it shares the amount, currency,
   *  payment methods, appearance and `submit()` orchestration.
   */
  const ExpressCheckoutElement = defineComponent({
    name: 'TixStripeExpressCheckoutElement',
    props: {
      options: {
        type: Object as PropType<StripeExpressCheckoutElementOptions>,
        required: false,
      },
    },
    emits: {
      ready: (event: StripeExpressCheckoutElementReadyEvent) => event.elementType === 'expressCheckout',
      click: (event: StripeExpressCheckoutElementClickEvent) => event.elementType === 'expressCheckout',
      confirm: (event: StripeExpressCheckoutElementConfirmEvent) => event.expressPaymentType != null,
      cancel: null,
      focus: null,
      blur: null,
      escape: null,
    },
    setup(props, { emit }) {
      const mountTarget = ref<HTMLDivElement | null>(null)
      let expressCheckoutElement: StripeExpressCheckoutElement | null = null

      onMounted(mountExpressCheckoutElement)
      watch(elements, (instance) => (instance ? mountExpressCheckoutElement() : teardownExpressCheckoutElement()))
      onBeforeUnmount(teardownExpressCheckoutElement)

      function mountExpressCheckoutElement(): void {
        /* v8 ignore next - defensive re-entrancy guard, as per the Payment Element -- @preserve */
        if (expressCheckoutElement) return

        const stripeElementsInstance = elements.value
        if (!stripeElementsInstance) return

        const target = mountTarget.value
        /* v8 ignore if - `target` can't be null while mounted; guarded rather than asserted -- @preserve */
        if (!target) {
          fatalError.value = new Error('Cannot mount express checkout element')
          elements.value = null
          return
        }

        expressCheckoutElement = stripeElementsInstance.create('expressCheckout', {
          ...props.options,
          // The Payment Element collects no billing details, so the wallet is our
          // only source for the buyer's email and address. After the spread so a
          // caller can't disable them.
          emailRequired: true,
          billingAddressRequired: true,
        })

        expressCheckoutElement.mount(target)
        expressCheckoutElement.on('ready', (event) => emit('ready', event))
        expressCheckoutElement.on('click', (event) => {
          event.resolve()
          emit('click', event)
        })
        expressCheckoutElement.on('confirm', (event) => emit('confirm', event))
        expressCheckoutElement.on('cancel', (event) => emit('cancel', event))
        expressCheckoutElement.on('focus', (event) => emit('focus', event))
        expressCheckoutElement.on('blur', (event) => emit('blur', event))
        expressCheckoutElement.on('escape', (event) => emit('escape', event))
        expressCheckoutElement.on('loaderror', (event) => {
          fatalError.value = new Error(event.error.message ?? `Stripe element load error (${event.error.type})`)
          elements.value = null
        })
      }

      function teardownExpressCheckoutElement(): void {
        if (expressCheckoutElement) {
          expressCheckoutElement.unmount()
          expressCheckoutElement.destroy()
          expressCheckoutElement = null
        }
      }

      return () => h('div', { ref: mountTarget })
    },
  })

  /**
   *  Stripe actions — pre-bound wrappers over the live handles.
   */
  const { submit, createPaymentMethod, confirmPayment } = createStripeActions({ stripe, elements, status })

  return {
    PaymentElement,
    ExpressCheckoutElement,
    submit,
    createPaymentMethod,
    confirmPayment,
    fatalError: readonly(fatalError),
    status,
  }
}
